// Service pour l'intégration Google Calendar
import { captureError, SEVERITY } from './errorService';

// Helper pour extraire le texte d'une instruction (peut être string ou objet)
const getInstructionText = (instruction) => {
  if (!instruction) return '';
  if (typeof instruction === 'string') return instruction;
  if (typeof instruction === 'object' && instruction.text) return instruction.text;
  if (typeof instruction === 'object' && instruction.name) return instruction.name;
  return '';
};

const SCOPES = 'https://www.googleapis.com/auth/calendar.events';
const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';
const TOKEN_STORAGE_KEY = 'adhd-planner-google-token';

class GoogleCalendarService {
  constructor() {
    this.tokenClient = null;
    this.gapiInited = false;
    this.gisInited = false;
    this.isSignedIn = false;
    this.onSignInChange = null;
    this.autoImportEnabled = false;
    this.lastSyncDate = null;
    this.importedEvents = new Map(); // Cache des événements importés par date
    this.importTimer = null;
    this.consecutiveErrors = 0; // Compteur d'erreurs consécutives
    this.maxConsecutiveErrors = 3; // Arrêter après 3 erreurs
  }

  // Sauvegarder le token dans localStorage
  saveToken(token) {
    try {
      const tokenData = {
        ...token,
        savedAt: Date.now()
      };
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    } catch (e) {
      console.warn('Erreur sauvegarde token Google:', e);
    }
  }

  // Récupérer le token depuis localStorage
  getSavedToken() {
    try {
      const data = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!data) return null;

      const token = JSON.parse(data);

      // Vérifier si le token n'est pas expiré (1 heure par défaut)
      const expiresIn = token.expires_in || 3600;
      const expiresAt = token.savedAt + (expiresIn * 1000);

      if (Date.now() >= expiresAt) {
        // Token expiré, le supprimer
        this.clearSavedToken();
        return null;
      }

      return token;
    } catch (e) {
      console.warn('Erreur lecture token Google:', e);
      return null;
    }
  }

  // Effacer le token sauvegardé
  clearSavedToken() {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  // Initialiser l'API Google
  async init(onSignInChange) {
    this.onSignInChange = onSignInChange;
    
    return new Promise((resolve, reject) => {
      // Charger le script GAPI
      const gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      gapiScript.onload = () => this.initGapi(resolve, reject);
      document.body.appendChild(gapiScript);

      // Charger le script GIS (Google Identity Services)
      const gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.async = true;
      gisScript.defer = true;
      gisScript.onload = () => this.initGis();
      document.body.appendChild(gisScript);
    });
  }

  async initGapi(resolve, reject) {
    try {
      await new Promise((res) => window.gapi.load('client', res));
      await window.gapi.client.init({
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY || process.env.REACT_APP_GOOGLE_API_KEY,
        discoveryDocs: [DISCOVERY_DOC],
      });
      this.gapiInited = true;

      // Restaurer le token sauvegardé s'il existe
      const savedToken = this.getSavedToken();
      if (savedToken) {
        window.gapi.client.setToken(savedToken);
        this.isSignedIn = true;
        this.autoImportEnabled = true;
        console.log('Token Google restauré, vérification en cours...');
        if (this.onSignInChange) this.onSignInChange(true);
        // Ne pas démarrer l'import automatique immédiatement
        // Laisser le premier appel valider le token
        this.startEventImport();
      }

      this.checkReady(resolve);
    } catch (error) {
      captureError(error, 'init GAPI', SEVERITY.BACKGROUND);
      reject(error);
    }
  }

  initGis() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId === 'votre_client_id.apps.googleusercontent.com') {
      console.warn('Google Client ID non configuré');
      return;
    }

    this.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: (response) => {
        if (response.error) {
          captureError(new Error(response.error), 'Google auth callback', SEVERITY.USER_FACING);
          return;
        }
        this.isSignedIn = true;
        this.autoImportEnabled = true;
        this.startEventImport();
        if (this.onSignInChange) this.onSignInChange(true);
      },
    });
    this.gisInited = true;
  }

  checkReady(resolve) {
    if (this.gapiInited) {
      resolve();
    }
  }

  // Se connecter à Google
  signIn() {
    if (!this.tokenClient) {
      captureError(new Error('Google non initialisé'), 'signIn', SEVERITY.USER_FACING);
      return Promise.reject(new Error('Google non initialisé'));
    }

    return new Promise((resolve, reject) => {
      try {
        // Demander un nouveau token
        this.tokenClient.callback = (response) => {
          if (response.error) {
            reject(response);
            return;
          }

          // Sauvegarder le token pour persistance
          const token = window.gapi.client.getToken();
          if (token) {
            this.saveToken(token);
          }

          this.isSignedIn = true;
          this.autoImportEnabled = true;
          this.startEventImport();
          if (this.onSignInChange) this.onSignInChange(true);
          resolve(response);
        };

        if (window.gapi.client.getToken() === null) {
          this.tokenClient.requestAccessToken({ prompt: 'consent' });
        } else {
          this.tokenClient.requestAccessToken({ prompt: '' });
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  // Se déconnecter
  signOut() {
    const token = window.gapi.client.getToken();
    if (token !== null) {
      window.google.accounts.oauth2.revoke(token.access_token);
      window.gapi.client.setToken('');
      this.clearSavedToken(); // Effacer le token sauvegardé
      this.isSignedIn = false;
      this.autoImportEnabled = false;
      this.stopEventImport();
      if (this.onSignInChange) this.onSignInChange(false);
    }
  }


  // Démarrer l'importation automatique des événements
  startEventImport() {
    // Don't start if disabled
    if (!this.autoImportEnabled) {
      console.log('Google Calendar: import désactivé, pas de démarrage');
      return;
    }

    // Clear any existing timer
    if (this.importTimer) {
      clearInterval(this.importTimer);
      this.importTimer = null;
    }

    console.log('Google Calendar: démarrage import automatique');

    // Importer les événements toutes les 60 secondes (quasi temps réel)
    this.importTimer = setInterval(() => {
      if (this.autoImportEnabled) {
        this.importTodayEvents();
      } else {
        this.stopEventImport();
      }
    }, 60000);

    // Import immédiat (délai pour laisser GAPI s'initialiser)
    setTimeout(() => {
      if (this.autoImportEnabled) {
        this.importTodayEvents();
      }
    }, 1000);
  }

  // Arrêter l'importation automatique
  stopEventImport() {
    if (this.importTimer) {
      console.log('🛑 Google Calendar: arrêt import automatique');
      clearInterval(this.importTimer);
      this.importTimer = null;
    }
    this.autoImportEnabled = false;
  }

  // Importer les événements d'aujourd'hui
  async importTodayEvents() {
    // Skip if already stopped or no token
    if (!this.autoImportEnabled) {
      return;
    }

    const token = window.gapi?.client?.getToken?.();
    if (!token) {
      console.log('Google Calendar: pas de token, arrêt import');
      this.autoImportEnabled = false;
      this.stopEventImport();
      return;
    }

    try {
      const today = new Date();
      // Pass true to throw on critical errors (401, 403, 404)
      await this.importEventsForDate(today, true);
      // Réinitialiser le compteur si succès
      this.consecutiveErrors = 0;
    } catch (error) {
      this.consecutiveErrors++;
      console.warn(`Erreur importation événements (${this.consecutiveErrors}/${this.maxConsecutiveErrors}):`, error?.message || error);

      const status = error?.status || error?.result?.error?.code;

      // 403 = Calendar API non activée OU token sans scope calendar
      // NE PAS déconnecter l'utilisateur, juste arrêter l'auto-import
      if (status === 403) {
        console.warn('🛑 Google Calendar API 403 — API non activée ou scope manquant. Arrêt auto-import.');
        console.warn('👉 Activer Calendar API : https://console.cloud.google.com/apis/library/calendar-json.googleapis.com');
        this.autoImportEnabled = false;
        this.stopEventImport();
        return;
      }

      // 401 = token expiré → déconnecter
      if (status === 401) {
        console.warn('🛑 Google Calendar: Token expiré (401), déconnexion');
        this.clearSavedToken();
        this.isSignedIn = false;
        this.autoImportEnabled = false;
        this.stopEventImport();
        if (this.onSignInChange) this.onSignInChange(false);
        return;
      }

      // 404 = API non trouvée
      if (status === 404) {
        console.warn('🛑 Google Calendar API introuvable (404), arrêt auto-import');
        this.autoImportEnabled = false;
        this.stopEventImport();
        return;
      }

      // Arrêter après trop d'erreurs consécutives
      if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
        console.warn('🛑 Trop d\'erreurs consécutives, arrêt de l\'import automatique Google Calendar');
        this.autoImportEnabled = false;
        this.stopEventImport();
      }
    }
  }

  // Importer les événements pour une date spécifique
  async importEventsForDate(date, throwOnCriticalError = false) {
    const hasToken = window.gapi?.client?.getToken?.();
    if (!hasToken) return [];

    try {
      // Importer depuis TOUS les calendriers de l'utilisateur
      const events = await this.getAllCalendarEvents(date);
      const convertedTasks = this.convertEventsToTasks(events, date);

      // Stocker dans le cache avec la date comme clé
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const dateKey = `${y}-${m}-${d}`;
      this.importedEvents.set(dateKey, convertedTasks);

      // Notifier l'app du changement
      if (this.onEventsImported) {
        this.onEventsImported(dateKey, convertedTasks);
      }

      console.log(`${convertedTasks.length} événements importés (tous calendriers) pour le ${dateKey}`);
      return convertedTasks;
    } catch (error) {
      console.warn('Erreur importation événements pour', date, error);
      const status = error?.status || error?.result?.error?.code;
      if (throwOnCriticalError && (status === 401 || status === 403 || status === 404)) {
        throw error;
      }
      return [];
    }
  }

  // Convertir les événements Google en tâches de l'app
  convertEventsToTasks(events, date) {
    return events
      .filter(event => {
        return event.start?.dateTime && // Seulement les événements avec heure
               !event.transparency; // Ignorer les événements "libre"
      })
      .map(event => {
        const start = new Date(event.start.dateTime);
        const end = new Date(event.end.dateTime);
        const duration = Math.round((end - start) / 60000); // en minutes

        return {
          id: `google-${event.id}`,
          task: event.summary || 'Événement sans titre',
          desc: event.description || `Importé de Google Calendar (${duration} min)`,
          time: start.toTimeString().slice(0, 5), // Format HH:MM
          duration: duration,
          category: this.getCategoryFromEvent(event),
          iconName: this.getIconFromEvent(event),
          points: Math.max(Math.floor(duration / 10), 5), // Points basés sur la durée
          isImported: true,
          googleEventId: event.id,
          instructions: event.description ? [event.description] : []
        };
      })
      .sort((a, b) => a.time.localeCompare(b.time)); // Trier par heure
  }

  // Déduire la catégorie d'un événement Google
  getCategoryFromEvent(event) {
    const summary = (event.summary || '').toLowerCase();
    const description = (event.description || '').toLowerCase();
    const text = summary + ' ' + description;

    // Mots-clés pour déterminer la catégorie
    if (text.match(/\b(médicament|pilule|docteur|médecin|pharmacie|rdv|rendez-vous|dentiste)\b/)) {
      return 'vital';
    }
    if (text.match(/\b(travail|réunion|meeting|call|bureau|projet|client|formation)\b/)) {
      return 'chore';
    }
    if (text.match(/\b(loisir|film|ami|restaurant|sport|vacances|weekend|détente)\b/)) {
      return 'pleasure';
    }
    if (text.match(/\b(cadeau|récompense|achat|shopping|plaisir)\b/)) {
      return 'reward';
    }

    // Par défaut, selon l'heure
    const hour = new Date(event.start.dateTime).getHours();
    if (hour >= 7 && hour <= 9) return 'vital'; // Matin = vital
    if (hour >= 9 && hour <= 17) return 'chore'; // Journée = travail
    return 'pleasure'; // Soirée = plaisir
  }

  // Déduire l'icône d'un événement Google
  getIconFromEvent(event) {
    const summary = (event.summary || '').toLowerCase();
    
    // Correspondances spécifiques
    const iconMap = {
      'repas|déjeuner|dîner|petit-déjeuner|restaurant|manger': 'Utensils',
      'sport|gym|course|vélo|natation|fitness': 'Dumbbell',
      'médecin|docteur|dentiste|pharmacie|rdv': 'Pill',
      'travail|réunion|meeting|bureau|projet': 'Briefcase',
      'film|cinéma|série|netflix': 'Youtube',
      'douche|bain|toilette': 'ShowerHead',
      'appel|téléphone|call': 'Phone',
      'café|thé|boisson': 'Coffee',
      'réveil|lever|matin': 'Sun',
      'coucher|dormir|sieste': 'Bed'
    };

    for (const [keywords, icon] of Object.entries(iconMap)) {
      if (summary.match(new RegExp(`\\b(${keywords})\\b`))) {
        return icon;
      }
    }

    return 'Calendar'; // Icône par défaut
  }

  // Obtenir les événements importés pour une date
  getImportedEventsForDate(dateString) {
    return this.importedEvents.get(dateString) || [];
  }

  // Callback pour notifier l'app des nouveaux événements
  setOnEventsImported(callback) {
    this.onEventsImported = callback;
  }



  // Récupérer la liste de tous les calendriers Google
  async listCalendars() {
    const token = window.gapi?.client?.getToken?.();
    if (!token) {
      const err = new Error('Non connecté à Google Calendar');
      err.status = 401;
      throw err;
    }

    try {
      const response = await window.gapi.client.calendar.calendarList.list();
      return (response.result.items || []).map(cal => ({
        id: cal.id,
        summary: cal.summary,
        primary: !!cal.primary,
        backgroundColor: cal.backgroundColor,
        selected: cal.selected !== false,
        accessRole: cal.accessRole,
      }));
    } catch (error) {
      captureError(error, 'listCalendars', SEVERITY.BACKGROUND);
      throw error;
    }
  }

  // Récupérer les événements d'une journée (d'un ou plusieurs calendriers)
  async getEventsForDay(date, calendarId = 'primary') {
    // Vérifier si GAPI a un token
    const token = window.gapi?.client?.getToken?.();
    if (!token) {
      const err = new Error('Non connecté à Google Calendar');
      err.status = 401;
      throw err;
    }

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    try {
      const response = await window.gapi.client.calendar.events.list({
        calendarId: calendarId,
        timeMin: startOfDay.toISOString(),
        timeMax: endOfDay.toISOString(),
        singleEvents: true,
        orderBy: 'startTime',
      });
      return response.result.items || [];
    } catch (error) {
      // Extraire le code d'erreur HTTP si disponible
      const status = error?.status || error?.result?.error?.code;
      const errorObj = new Error(error?.result?.error?.message || error?.message || 'Erreur API Google Calendar');
      errorObj.status = status;
      captureError(errorObj, 'récupération événements Google Calendar', SEVERITY.BACKGROUND);
      throw errorObj;
    }
  }

  // Récupérer les événements de TOUS les calendriers pour une journée
  async getAllCalendarEvents(date) {
    try {
      const calendars = await this.listCalendars();
      const allEvents = [];

      for (const cal of calendars) {
        if (!cal.selected) continue;
        // Ignorer les calendriers en lecture seule sans accessRole 'owner'/'writer'
        // sauf si c'est le calendrier principal
        try {
          const events = await this.getEventsForDay(date, cal.id);
          events.forEach(ev => {
            ev._calendarName = cal.summary;
            ev._calendarColor = cal.backgroundColor;
          });
          allEvents.push(...events);
        } catch (e) {
          // 403 = pas d'accès à ce calendrier, skip silencieusement
          if (e.status === 403 || e.status === 404) continue;
          console.warn(`Skip calendar "${cal.summary}":`, e.message);
        }
      }

      return allEvents;
    } catch (error) {
      // 403 sur listCalendars = API non activée, fallback primary
      const status = error?.status || error?.result?.error?.code;
      if (status === 403) {
        console.warn('Calendar API 403 — fallback primary. Activer Calendar API sur GCP.');
        try {
          return await this.getEventsForDay(date, 'primary');
        } catch (e2) {
          // Même primary échoue — API pas activée
          if (e2.status === 403) {
            console.error('Calendar API non activée. Allez sur https://console.cloud.google.com/apis/library/calendar-json.googleapis.com');
            return [];
          }
          throw e2;
        }
      }
      console.warn('Fallback to primary calendar:', error.message);
      return this.getEventsForDay(date, 'primary');
    }
  }

  // Créer un événement dans Google Calendar
  async createEvent(task, date) {
    // Vérifier si GAPI a un token
    const hasToken = window.gapi?.client?.getToken?.();
    if (!hasToken) {
      throw new Error('Non connecté à Google Calendar');
    }

    try {
      // Parser l'heure de la tâche
      const [hours, minutes] = (task.time || '09:00').split(':').map(Number);
      const duration = task.duration || 30;

      // Créer les dates de début et fin
      const startDate = new Date(date);
      startDate.setHours(hours, minutes, 0, 0);

      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + duration);

      // Construire la description
      const description = [
        task.desc || '',
        task.instructions?.length ? `\n📝 Étapes:\n${task.instructions.map((i, idx) => `${idx + 1}. ${getInstructionText(i)}`).join('\n')}` : '',
        `\n\n🏷️ Catégorie: ${task.category || 'tâche'}`,
        `⏱️ Durée: ${duration} min`,
        '\n\n📱 Créé depuis ADHD Planner'
      ].filter(Boolean).join('');

      // Couleur selon la catégorie
      const colorId = {
        vital: '11',    // Rouge
        chore: '5',     // Jaune
        pleasure: '10', // Vert
        reward: '3'     // Violet
      }[task.category] || '8';

      const event = {
        summary: `[${(task.category || 'tâche').toUpperCase()}] ${task.task}`,
        description: description,
        start: {
          dateTime: startDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: endDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        colorId: colorId,
        reminders: {
          useDefault: false,
          overrides: [
            { method: 'popup', minutes: 10 }
          ]
        }
      };

      const response = await window.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event
      });

      console.log('Événement créé:', response.result.id);
      return response.result;
    } catch (error) {
      captureError(error, 'création événement Google Calendar', SEVERITY.USER_FACING);
      throw error;
    }
  }

  // PUSH: Envoyer les tâches locales vers Google Calendar
  async pushTasksToCalendar(tasks, date) {
    // Vérifier si GAPI a un token (via Firebase Auth ou GIS)
    const hasToken = window.gapi?.client?.getToken?.();
    if (!hasToken) {
      return { pushed: 0, errors: [], message: 'Non connecté' };
    }

    const results = { pushed: 0, errors: [], skipped: 0 };

    // Récupérer les événements existants pour éviter les doublons
    const existingEvents = await this.getEventsForDay(date);
    const existingTitles = existingEvents.map(e => e.summary?.toLowerCase());

    for (const task of tasks) {
      // Ignorer les tâches importées de Google Calendar
      if (task.isImported) {
        results.skipped++;
        continue;
      }

      // Vérifier si la tâche existe déjà
      const taskTitle = `[${(task.category || 'tâche').toUpperCase()}] ${task.task}`.toLowerCase();
      if (existingTitles.includes(taskTitle)) {
        results.skipped++;
        continue;
      }

      try {
        await this.createEvent(task, date);
        results.pushed++;
      } catch (error) {
        results.errors.push({ task: task.id, error: error.message });
      }
    }

    results.message = results.pushed > 0
      ? `${results.pushed} tâche(s) envoyée(s) vers Google Calendar`
      : results.skipped > 0
        ? 'Tâches déjà synchronisées'
        : 'Aucune tâche à synchroniser';

    return results;
  }

  // PULL: Forcer l'importation des événements
  async pullEventsFromCalendar(date) {
    // Vérifier si GAPI a un token (via Firebase Auth ou GIS)
    const hasToken = window.gapi?.client?.getToken?.();
    if (!hasToken) {
      return { pulled: 0, events: [], message: 'Non connecté' };
    }

    try {
      const events = await this.importEventsForDate(date);
      return {
        pulled: events.length,
        events: events,
        message: events.length > 0
          ? `${events.length} événement(s) importé(s)`
          : 'Aucun événement trouvé'
      };
    } catch (error) {
      return { pulled: 0, events: [], message: 'Erreur: ' + error.message };
    }
  }

}

// Singleton
const googleCalendarService = new GoogleCalendarService();
export default googleCalendarService;
